import {
  Theme,
  RequiredTheme,
  ThemeValue,
  ResponsiveValue,
  compose,
  system,
  TextColorProps,
  color as styledColor,
  ColorProps as StyledColorProps,
  TypographyProps as StyledTypographyProps,
  BorderProps as StyledBorderProps,
} from 'styled-system';
import * as CSS from 'csstype';
import { SolvSystemProps } from './system';

/**
 * MARK: Box Shadow Props
 * ================
 */
interface BoxShadowProps<
  ThemeType extends Theme = RequiredTheme,
  TVal = ThemeValue<'shadows', ThemeType>,
> {
  /**
   * The box-shadow CSS property adds shadow effects around an element's frame. You can set multiple effects separated
   * by commas. A box shadow is described by X and Y offsets relative to the element, blur and spread radii and color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/box-shadow)
   */
  boxShadow?: ResponsiveValue<TVal, ThemeType>;
}

interface TextShadowProps<
  ThemeType extends Theme = RequiredTheme,
  TVal = ThemeValue<'shadows', ThemeType>,
> {
  /**
   * The `text-shadow` CSS property adds shadows to text. It accepts a comma-separated list of shadows to be applied
   * to the text and any of its `decorations`. Each shadow is described by some combination of X and Y offsets from
   * the element, blur radius, and color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/text-shadow)
   */
  textShadow?: ResponsiveValue<TVal, ThemeType>;
}

/**
 * Custom shadow props to pull values from our theme (whereas styled system assumes they are just array indecies by default)
 */
export interface ShadowProps<ThemeType extends Theme = RequiredTheme>
  extends BoxShadowProps<ThemeType>,
    TextShadowProps<ThemeType> {}

/**
 * MARK: Typography Props
 */

interface FontFamilyProps<
  ThemeType extends Theme = RequiredTheme,
  TVal = ThemeValue<'fonts', ThemeType>,
> {
  fontFamily?: ResponsiveValue<TVal, ThemeType>;
}

export interface TypographyProps<ThemeType extends Theme = RequiredTheme>
  extends Omit<StyledTypographyProps<ThemeType>, 'fontFamily'>,
    FontFamilyProps<ThemeType> {}

/**
 * MARK: Shadow Props
 * ==================
 */

/**
 * Styled-system patch for the color prop fixing "Types of property 'color' are incompatible"
 * when appling props to component that extend ColorProps.
 */
export interface ColorProps<T> extends Omit<StyledColorProps<T>, 'color'> {
  textColor?: TextColorProps<T>['color'];
}

/**
 * The updated styled system integration with `textColor` instead of `color`
 */
export const color = compose(
  styledColor,
  system({
    // Alias color as textColor
    textColor: {
      property: 'color',
      scale: 'colors',
    },
  }),
);

/**
 * Mark: Border Props
 * =================
 */

interface BorderWidthProps {
  borderWidth?: ResponsiveValue<CSS.Property.BorderWidth>;
}

/**
 * Border props with fix for `borderWidth` allowing any valid CSS value
 */
export interface BorderProps<ThemeType extends Theme = RequiredTheme>
  extends Omit<StyledBorderProps<ThemeType>, 'borderWidth'>,
    BorderWidthProps {}

type SolvCSSObjectFlat = Omit<CSS.Properties, keyof SolvSystemProps> &
  SolvSystemProps &
  Record<string, any>;

type NestedCSS = Partial<Record<CSS.Pseudos & string, SolvCSSObjectFlat>>;

/**
 * This is a css object that also supports solv system keys
 */
export type SolvCSSObject = SolvCSSObjectFlat & NestedCSS;

/**
 * A variant object containing variant keys mapped to a css object
 */
export type Variant<Keys extends string> = Record<Keys, SolvCSSObject>;
